import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useClientId } from '../../../../../../../../_hooks/useClientId';
import { formatCurrency } from '../../../../../../../../CommonUtils/formatNumber';
import { useFpId } from '../../../../../../../hooks/useFpId';
import {
  whatIfsBodySelector,
  whatIfsOrDefaultSelector,
} from '../../../../../../../../WhatIfs/WhatIfsSelector';
import {
  SocialSecurityStrategy,
  useAnnualBenefitsSummaryQuery,
} from '../../api/useAnnualBenefitsSummaryQuery';
import { useAnnualBenefitsSummaryWithWhatIfsMutation } from '../../api/useAnnualBenefitsSummaryWithWhatIfsMutation';
import { useSocialSecurityWhatIfRefreshToken } from '../../api/useSocialSecurityWhatIfRefresh';

interface Props {
  strategy: SocialSecurityStrategy;
}

const sliceFrom = (values: number[] | null, start: number): number[] | null => {
  return values?.slice(start) ?? null;
};

export const useBenefitsModal = ({ strategy }: Props) => {
  const clientId = useClientId();
  const fpId = useFpId();
  const whatIfRefreshToken = useSocialSecurityWhatIfRefreshToken(clientId, fpId);

  const whatIfsBody = useSelector(whatIfsBodySelector);
  const isWhatIfsOpen = useSelector(whatIfsOrDefaultSelector).open;
  const isWhatIfsActive = isWhatIfsOpen && whatIfsBody != null;

  const {
    isFetching: isQueryFetching,
    data: queryData,
    error: queryError,
    refetch,
  } = useAnnualBenefitsSummaryQuery({
    clientId,
    fpId,
    strategy,
    enabled: !isWhatIfsActive,
  });

  const mutation = useAnnualBenefitsSummaryWithWhatIfsMutation({ clientId, fpId, strategy });
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (isWhatIfsActive) {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      mutation.mutate({ clientId, fpId, strategy, body: whatIfsBody, signal: controller.signal });
    }
  }, [isWhatIfsActive, whatIfsBody, clientId, fpId, strategy, whatIfRefreshToken]);

  const isFetching = isWhatIfsActive ? mutation.isLoading : isQueryFetching;
  const rawData = isWhatIfsActive ? mutation.data : queryData;
  const error = isWhatIfsActive ? mutation.error : queryError;

  const data = useMemo(() => {
    if (!rawData) {
      return null;
    }

    const { PrincipalName, SpouseName } = rawData;

    const hasPrincipal =
      PrincipalName != null && rawData.PrincipalAges != null && rawData.PrincipalBenefits != null;
    const hasSpouse =
      SpouseName != null && rawData.SpouseAges != null && rawData.SpouseBenefits != null;

    const start = Math.max(
      0,
      rawData.Years.findIndex(
        (_, i) =>
          (hasPrincipal && (rawData.PrincipalBenefits?.[i] ?? 0) > 0) ||
          (hasSpouse && (rawData.SpouseBenefits?.[i] ?? 0) > 0),
      ),
    );

    const Years = rawData.Years.slice(start);
    const PrincipalAges = sliceFrom(rawData.PrincipalAges, start);
    const PrincipalBenefits = sliceFrom(rawData.PrincipalBenefits, start);
    const SpouseAges = sliceFrom(rawData.SpouseAges, start);
    const SpouseBenefits = sliceFrom(rawData.SpouseBenefits, start);

    const chart = {
      principal: hasPrincipal ? { ages: PrincipalAges, benefits: PrincipalBenefits } : null,
      spouse: hasSpouse ? { ages: SpouseAges, benefits: SpouseBenefits } : null,
    };

    const baseAges = hasPrincipal ? PrincipalAges! : SpouseAges ?? [];
    const ages = baseAges.map((_, index) => {
      if (hasPrincipal && hasSpouse) {
        return `${PrincipalAges![index]}/${SpouseAges![index]}`;
      }

      if (hasPrincipal) {
        return PrincipalAges![index];
      }

      return SpouseAges![index];
    });

    const principalRow = hasPrincipal
      ? PrincipalBenefits!.map(value => formatCurrency(value))
      : null;
    const spouseRow = hasSpouse ? SpouseBenefits!.map(value => formatCurrency(value)) : null;

    return {
      ages,
      chart,
      principalName: PrincipalName,
      principalRow,
      spouseName: SpouseName,
      spouseRow,
      years: Years,
    };
  }, [rawData]);

  return {
    isFetching,
    data,
    error,
    refetch,
  };
};
